import _ from 'lodash';
import mixins from 'vue-typed-mixins';
import { ActionHandler, ActionTree, GetterTree, mapActions, Module, MutationTree } from 'vuex';
import { Route, RawLocation } from 'vue-router';
import Vue, { VueConstructor } from 'vue';
import { ComputedOptions } from 'vue/types/options.d';
import { RootStore } from '@/main/store/@types/store.d';
import { PageModeMixin } from '@/main/mixins/pageMode';
import { ERROR, PAGE_MGR_BACK_TO, PAGE_MGR_SET } from '@/main/store/actionTypes';
import { EventBus } from '@/main/eventBus';
import { DataMapper, PageInfo, PageManager } from '@/main/store/pageManager';
import { isNotNull } from '@/main/utils/isNotNull';
import { SET_PROPS, START_LOADING, END_LOADING  } from '@/main/store/listEditTypes';
import { PageStore, PageStoreBase, PageStoreProps } from '@/main/store/@types/page';
import { DropdownList } from '@/main/store/@types/dropdown.d';
import { OperationsMixin } from '@/main/mixins/operations';
import { OpenerMixin } from '@/main/mixins/opener';
import { apiProvider } from '@/main/pages/api';

// eslint-disable-next-line no-use-before-define
type PageType = InstanceType<typeof Page>;

export function getterAndSetterFromKeyDvm<T>(key: string, object: any): ComputedOptions<T> {
	return {
		get(this: PageType): any {
			return this.$store.getters[`${this.storeUri}/getField`](key);
		},
		set(this: PageType, value: any): void {
			this.$store.commit(`${this.storeUri}/updateField`, {
				path: key,
				value,
			});
		},
	};
}
export function getterFromKeyDvm<T>(key: string, object: any): ComputedOptions<T> {
	return {
		get(this: PageType): any {
			return this.$store.getters[`${this.storeUri}/${key}`];
		},
	};
}

export type MappedGetters<Getters extends GetterTree<any, RootStore>> =
	{ [K in keyof Getters]: Getters[K] extends (...args: any) => infer R ? R : unknown };

type MutationPayloadArr<T> = T extends (state: any, ...payload: infer P) => any ? P : never;

// Object containing methods that call mutations.
type MappedMutations<T extends MutationTree<any>> =
	{ [key in keyof T]: (this: Vue & { storeUri: string | null }, ...payload: MutationPayloadArr<T[key]>) => void };

export function mapMutationsDvm<Mutations extends MutationTree<any>>(mutations: Mutations): MappedMutations<Mutations> {
	return (Object.keys(mutations) as (keyof Mutations)[])
		.reduce((obj, key) => {
			obj[key] = function (this: Vue & { storeUri: string | null }, ...args: MutationPayloadArr<Mutations[typeof key]>) {
				return this.$store.commit(`${this.storeUri}/${key}`, ...args);
			};

			return obj;
		}, {} as MappedMutations<Mutations>);
}

// Helper types for `MappedActions`.
type ActionHandlerPayloadArr<T> = T extends (actionContext: any, ...payload: infer P) => any ? P : never;
type ActionObjectPayloadArr<T> = T extends { handler: (actionContext: any, ...payload: infer P) => any } ? P : never;

type ActionPayloadArr<T> = T extends ActionHandler<any, any> ? ActionHandlerPayloadArr<T> : ActionObjectPayloadArr<T>;

type ActionHandlerReturnType<T> = T extends (...args: any) => infer R ? R : never;
type ActionObjectReturnType<T> = T extends { handler: (...args: any) => infer R } ? R : never;

type ActionReturnType<T> = T extends ActionHandler<any, any> ? ActionHandlerReturnType<T> : ActionObjectReturnType<T>;

// Object containing methods that call actions.
type MappedActions<T extends ActionTree<any, any>> =
	{ [key in keyof T]: (this: Vue & { storeUri: string | null }, ...payload: ActionPayloadArr<T[key]>) => Promise<ActionReturnType<T[key]>> };

export function mapActionsDvm<Actions extends ActionTree<any, any>>(actions: Actions) {
	return (Object.keys(actions) as (keyof Actions)[])
		.reduce((obj, key) => {
			obj[key] = function (this: Vue & { storeUri: string | null }, ...payload: ActionPayloadArr<Actions[typeof key]>) {
				return this.$store.dispatch(`${this.storeUri}/${key}`, ...payload);
			};

			return obj;
		}, {} as MappedActions<Actions>);
}

export function initStore<TKeys extends PageStoreBase>(vue: Vue, name: string, store: Module<TKeys, RootStore>) {
	const pageManager = vue.$store.state.pageManager as PageManager;
	const page = pageManager.pages.find((p) => p.path === vue.$router.currentRoute.path);

	const arrayThreeDigitStr = new Uint32Array(1);
	window.crypto.getRandomValues(arrayThreeDigitStr);
	const threeDigitStr = () => arrayThreeDigitStr[0].toString(10).substring(2, 5);

	let storeModule = `${name}-${threeDigitStr()}-${threeDigitStr()}`;

	if (page) {
		storeModule = page.storeModule!;
	} else {
		while ((<any>vue.$store.state).pages[storeModule as any]) {
			storeModule = `${name}-${threeDigitStr()}-${threeDigitStr()}`;
		}
	}

	// vue.$options.name = `${(<TKeys>store.state).name}_#${storeModule}`;
	if (!(<any>vue.$store.state.pages)[storeModule]) {
		(<any>vue.$store).registerModule(['pages', `${storeModule}`], store);

		vue.$store.commit(`pages/${storeModule}/${SET_PROPS}`, vue.$route.params);
		vue.$store.commit(
			`pageManager/${PAGE_MGR_SET}`,
			{
				name: vue.$options.name,
				path: vue.$route.path,
				storeModule,
				id: vue.$route.params.id,
				loadData: true,
			},
		);
	}
}

export function isRouteEvent(route: any): route is RouteEvent {
	return route.next !== undefined;
}

export function isBeforeUnloadEvent(event: any): event is BeforeUnloadEvent {
	return (<BeforeUnloadEvent>event).type !== undefined;
}

export interface RouteEvent {
	to: Route,
	from: Route,
	next: (to?: RawLocation | false | ((vm: Vue) => any) | void) => void,
}


const Mixins = mixins(PageModeMixin, OperationsMixin, OpenerMixin);

/**
 * The supertype of DMS components.
 *
 * @abstract use a page factory to create a sub-component.
 */
export const Page = (Mixins as VueConstructor<InstanceType<typeof Mixins> & PageStore<DropdownList, object, PageStoreProps>>).extend({
	name: 'BasePage',
	provide() {
		return { pageMode: this.pageMode };
	},
	data() {
		return {
			titleDetail: null as string | null,
			staticStoreModule: null as string | null,
			modulesToRemove: null as PageInfo[] | null,
		};
	},
	computed: {
		parentUri(): string | null {
			const { pages } = this.$store.state.pageManager;

			return pages.length > 1 ? `pages/${pages[pages.length - 2].storeModule}` : null;
		},
		storeModule(): string {
			return this.staticStoreModule ?? this.pageMgrPage?.storeModule!;
		},
		storeUri(): string | null {
			return this.storeModule ? `pages/${this.storeModule}` : null;
		},
		pageMgrPage(): PageInfo | null {
			return _.last(this.$store.state.pageManager.pages) || null;
		},
		/**
		 * Returns true if the component can be closed and page returned to the
		 * previous component.
		 */
		canClose() {
			return this.$store.state.pageManager.pages.length > 1;
		},
		isEditForm(): boolean {
			return this.isEditMode || this.isCreateMode;
		},
	},
	watch: {
		async $route() {
			await this.baseCreated();
		},
		/** Sets crumb detail. */
		titleDetail(val): void {
			this.crumb.detail = val;
		},
	},

	/** Calls {@link this.baseCreated}. */
	async created() {
		this.$store.commit(`${this.storeUri}/${START_LOADING}`);
		const allowed = await this.checkPagePermission();
		this.$store.commit(`${this.storeUri}/${END_LOADING}`);
		if (!allowed) {
			this.$message.error(this.LocalizeChyboveHlasky('NoAccessToPage'));
			//await this.$router.back();
			return;
		}
		this.loadingCount += 1;
		this.staticStoreModule = this.pageMgrPage?.storeModule!;
		await this.baseCreated();
		window.addEventListener('beforeunload', this.beforeUnload);
		this.loadingCount -= 1;
	},
	/** Emits the 'Mode' event. */
	updated() {
		EventBus.$emit('Mode', this.pageMode);
	},
	/** Emits the 'Mode' event. */
	mounted() {
		EventBus.$emit('Mode', this.pageMode);
	},
	async beforeDestroy() {
		window.removeEventListener('beforeunload', this.beforeUnload);
		const moduleRemoving = (element: PageInfo) => {
			this.$store.commit(`pages/${element.storeModule}/destroy`, element.storeModule);
			this.$store.unregisterModule(['pages', element.storeModule!]);
		};
		this.modulesToRemove && this.modulesToRemove.forEach(moduleRemoving);
	},
	methods: {
		...mapActions('pageManager', {
			pageMagrBack: PAGE_MGR_BACK_TO,
		}),
		/**
		 * Derives the role/permission identifier from a page URL path.
		 *
		 * URLs come in two shapes:
		 *   - Flat:    `/Entity/<mode>[/id]`                          e.g. `/Partner/List`
		 *   - Grouped: `/Container/Group/Entity/<mode>[/id]`          e.g. `/Ciselniky/Pujcovna/SkladoveMistoPredmetu/List`
		 *
		 * For grouped containers (`Ciselniky`, `Sestavy`) the role is the **third**
		 * path segment; otherwise the **second**. If the picked segment turns out
		 * to be a page-mode keyword (`List`, `Edit`, ...) we step back one segment
		 * — this handles flat entries inside a container like `/Ciselniky/LocationMaster/List`.
		 *
		 * The result is then mapped through an alias table that mirrors the
		 * `{ name, route }` entries from the menu files (e.g.
		 * [`src/modules/ciselnik/dilenske/menu.ts`](../../modules/ciselnik/dilenske/menu.ts)):
		 * the URL segment is the `route`, the backend permission key is the `name`.
		 */
		deriveRoleFromPath(path: string | undefined | null): string {
			const roleMap: Record<string, string> = {
				// Top-level / shared
				UzivatelskyProfil: 'UzivProfil',
				KategoriePracovnika: 'KategoriePrac',
				Partner: 'KZV_Zakaznici',
				Vozidlo: 'KZV_Vozidla',
				VozidloCheckIn: 'CheckIn',
				NdSklad: 'pAgendaSkladuND',
				Fakturace: 'Zakazka',
				// Ciselnik / Dilenske (see modules/ciselnik/dilenske/menu.ts)
				PpDruh: 'PpDruh',
				PpServiceNr: 'PpServiceNr',
				WCas: 'Casw',
				ZarazeniZakazky: 'SMVaria',
				OdlozenaServisniCinnost: 'pVozZavadyText',
				LakDruh: 'Lakdruh',
				ZakazkaKorekceTempl: 'ZakazkaKorekceTempl',
				Barva: 'Barva',
				TabulkovaPozice: 'Tablpoz',
				Typ: 'pTyp',
				InterniDruh: 'Internidruh',
				PracovniPozice: 'PracovniPozice',
				Interier: 'Interier',
				HodinoveSazby: 'hodinoveSazby',
				KategorieZarizeni: 'KatZarizeni',
				OkruhPp: 'OkruhyPP',
				Zarizeni: 'Zarizeni',
				ServisniPakety: 'ServisniPakety',
				VolitelnyMaterialSp: 'VolitelnyMaterialSp',
				IndividualniNahradyND: 'IndividualniNahradyND',
				VybavaVozidla: 'VybavaVoz',
				DruhSP: 'DruhSp',
				OdhadyPracnosti: 'OdhadyPracnosti',
				ZvlastniNabidky: 'ZvlastniNabidky',
				DruhPozadavku: 'DruhPozadavku',
				PpServisniCislo: 'PpServiceNr',
				PpCislo: 'PpCislo',
				NdCenik: 'pCenikND',
				NdEdl: 'EdlNd',
				NdPrijemka: 'ND_Prijem',
				PRInvoice: 'PRINVOICE',
				NdPohyb: 'ND_Pohyb',
				NdSkladPreceneni: 'ND_Preceneni',
				Inventura: 'Inventura',
				//PneuSklad: 'UdrzbaSkladPneu',
				PartsInventoryAdjustments: 'PARTS_INVEN_ADJUSTMENT',
				//PartReturnRequest: 'PART_RETURN',
				CasovaUdalost: 'UpravyCasu',
				//ReverseJobOrder: 'REVERSE_JOB_ORDER',
				ZakazkaArchiv: 'ARCHIV_ZAKAZEK',
				ZakazkaRezervaceNd: 'Servis_RezervaceND',
				Doklad: 'ARCHIV_DOKLADU',
				MontazDV: 'MONTAZDV',
				Capaket: 'PaketovyModul',
				ZakazkaList: 'Zakazka',
				HromadnaKorespondence: 'HromKoresp',
				ObchPrip: 'CRM_ObchPripady',
				Kampane: 'CRM_Kampane',
				ZakaznikKontakt: 'CRM_Kontakty',
				CustomerLeads: 'Customer_Leads',
				Leady: 'CRM_Leady',
				ArchyvDokladu: 'NV_ArchivDokl',
				VozyNaCeste: 'NV_NaCeste',
				ArchyvPohybuDokladu: 'NV_PohybDokl',
				SkladNovychVozu: 'pNvsklad',
				Nabidky: 'NV_Nabidky',
				ProdejNovychVozu: 'NV_Prodeje',
				PlanovanaPujckaVozidla: 'PlanovanaPujcka',
				VozidloPujcovna: 'APUJ_Vozidla',
				PredmetPujcovna: 'APUJ_OstatniPredmety',
				ReklamniText: 'Reklamatext',
				NakladoveStredisko: 'NaklStred',
				SkupinaDokladu: 'Dokladsk',
				DokladCiselnaRada: 'SpravaCR',
				//Texty: 'Texty',
				ZakazkaDruh: 'SZakazkadruh',
				SazbaDph: 'Dphsazba',
				PracovniTym: 'Tymprac',
				InvoiceChargeType: 'ChargeType',
				ZarazeniZakaznika: 'ZakVaria',
				ZakaznickaSkupina: 'pZakazniksk',
				ZakaznickyTitul: 'pZakTitul',
				InvoiceDepartment: 'Department',
				ZarazeniPredmetuPujcovny: 'OpaVaria',
				Pujcovna: 'APUJ_Pujcovna',
				ZarazeniVozidlaPujcovny: 'VpaVaria',
				DmsLogo: 'Logotypy',
				SpravciOsobnichUdaju: 'ZpracovOsDat',
				RozmerPneuDisk: 'pPDRozmer',
				VyskaPneu: 'pPneuvyska',
				VyrobcePneu: 'pVyrobekpneu',
				SkladPneu: 'pSkladpneu',
				SkladoveMistoPneu: 'pSkladmistoP',
				TypPneu: 'pTypPneu',
				SirkaPneu: 'pPneusirka',
				VyrobceDisku: 'pVyrobekdisk',
				SirkaDisku: 'pDisksirka',
				SlevaMaterialu: 'Slevamat',
				SlevaMaterialuSkupina: 'Slevamatsk',
				Zbozidruh: 'ZboziDruh',
				Sklad: 'SkladyNd',
				PseudoNdNahrada: 'pseudoNahradyNd',
				MarketovaSkupina: 'MarketoveSkupiny',
				SkladoveMisto: 'Skladmisto',
				MernaJednotka: 'MerneJednotky',
				ZakazkaZarazeni: 'SMVaria',
				VozZavadaText: 'pVozZavadyText',
				HodinovaSazba: 'hodinoveSazby',
				SpravaIndivNahradNd: 'IndividualniNahradyND',
				DruhLaku: 'Lakdruh',
				UzavUcetni: 'pUzaverkaUcto',
				UzavPokladen: 'pUzavPoklad',
				NdObjednavkaUzaverka: 'UzaverkaObjednavekNd',
				UzaverkaSkladu: 'UzaverkaSkladu',
				UzavRozpracovanosti: 'pUzaverkaRozprac',
				ServisniSestavy: 'StandTiskyDilna',
				SkladNdSestavy: 'StandSklad',
				ManagementSestavy: 'StandRepManager',
				ServiceOrderHistory: 'ServiceHistory',
				AccountReceivableReport: 'AccountReceivable',
				OutstandingRevenue: 'OutstandingRevenue',
				CashierReport: 'CashierReport',
				ZmenaHesla: 'ADM_Heslo',
				UserDefColumns: 'ADM_Pole',
				UpozorneniUdalost: 'ADM_NastaveniUpozornovani',
				ChybovyLog: 'ADM_ChybovyLog',
				SqlLog: 'ADM_SqlLog',
				CdbLog: 'ADM_CdbLog',
				ElektronickaFakturace: 'pEDIMap',
				HalerVyrov: 'HaleroveVyrovnani',
				LimCisRad: 'LimityCiselnychRad',
				ExportDat: 'ADM_NastaveniExportu',
				CaskPredplatne: 'ADM_CaskPredplatne',
				//ZakazkaRezervaceNd: 'Zakazka',
				// Pages opened from the Zakazka workflow — all inherit the Zakazka permission.
				// (see modules/servis/zakazka/EditBase.vue, EditLayout.vue, components/Buttons.vue, components/Saga2.vue, ...)
				ZakazkaNd: 'Zakazka',
				ZakazkaPracovniPozice: 'Zakazka',
				ZakazkaCiziVykony: 'Zakazka',
				ZakazkaKorekce: 'Zakazka',
				ZakazkaPneu: 'Zakazka',
				ZakazkaZapujcka: 'Zakazka',
				ZakazkaZaloha: 'Zakazka',
				ZakazkaDelbaPP: 'Zakazka',
				PartReturnRequest: 'PART_RETURN',
				ReverseJobOrder: 'REVERSE_JOB_ORDER',
				ServiceJobDetails: 'Zakazka',
				ServiceJobSplitBilling: 'Zakazka',
				SplitBilling: 'Zakazka',
				ServicePackage: 'Zakazka',
				ServisniPaket: 'Zakazka',
				Audatex: 'Zakazka',
				PendingJoblist: 'Zakazka',
				PneuSklad: 'Zakazka',
				NdObjednavkaZakazka: 'Zakazka',
				JednotnaFrontaNd: 'Zakazka',
				PROrdersParts: 'Zakazka',
				PaketovyModul: 'Zakazka',
				Texty: 'Zakazka',
				PendingRequestListIframe: 'PendingRequest',
				CPF: 'AssignCPF',
				CPFListIframe: 'CorporateProfile',
				SubSegmentCategory: 'SegmentSubCategory',
			};

			//const GROUPED_CONTAINERS = new Set(['Ciselniky', 'Sestavy', 'Sestavy']);
			const MODE_KEYWORDS = new Set(['List', 'SelectList', 'Create', 'Edit', 'View','List', 'selectList', 'create', 'edit', 'view',]);

			const segments = path?.split('/').slice(1) ?? [];
			const roleIndex = 1;
			let role = segments[roleIndex] ?? 'unknown';
			
			if(segments.length > 3) {
				role = segments[2] ?? 'unknown';
			}
			
			// Picked a mode keyword (flat entry inside a container) -> step back one.
			if (MODE_KEYWORDS.has(role)) {
				role = segments[roleIndex] ?? 'unknown';
			}
			
			if (role === 'Edit' || role === 'View') {
				role = segments[segments.length - 1] ?? 'unknown';
			}
			
			role = roleMap[role] ?? role;

			if (role === 'unknown') {
				role = 'ItemsReceivedDetail(GRN)';
			}
			return role;
		},
		/**
		 * Checks whether the current user is allowed to view the current page.
		 *
		 * If the check fails for the current page, retries with the root page's
		 * name (`pages[0]` in the page manager stack — the page from which the
		 * current navigation chain originated). This lets sub-pages reached via
		 * redirect inherit access from their opener.
		 */
		async checkPagePermission(): Promise<boolean> {
			const { pages } = this.$store.state.pageManager;
			
			const currentPage = pages[pages.length - 1] as PageInfo | undefined;
			const rootPage = pages.length > 1 ? (pages[0] as PageInfo) : null;

			let action = this.isEditMode ? '3' : this.isCreateMode ? '2' : this.isListMode ? '0' : '1';
			const userId = this.loggedUser.PracovnikData.PracovnikId!;

			const pathForCheck = rootPage?.path ?? currentPage?.path;
			const role = this.deriveRoleFromPath(pathForCheck);
			if(role === 'StandRepManager') {
				action = '0';
			}
			if(role === 'REVERSE_JOB_ORDER') {
				action = '0';
			}
			if(role === 'ARCHIV_DOKLADU') {
				action = '0';
			}
			if(role === 'StandSklad') {
				action = '0';
			}
			if(role === 'StandTiskyDilna') {
				action = '0';
			}
			if(role === 'CashierReport') {
				action = '1';
			}
			if(role === 'OutSourcingDetail') {
				action = '1';
			}
			if (role === 'OutstandingRevenue') {
				action = '1';
			}
			if(role === 'Zakazka' && action === '2') {
				action = '0';
			}
			const check = await apiProvider.CheckVP(userId, role, action);

			return !!check.data;
		},
		ancestorUri(indexFromEndOfArray: number): string | null {
			return this.storeModule || this.$store.state.pageManager.pages.length > 1
				? `pages/${this.$store.state.pageManager.pages[this.$store.state.pageManager.pages.length - indexFromEndOfArray].storeModule}`
				: null;
		},
		saveProps() {
			this.$store.commit(`${this.storeUri}/${SET_PROPS}`, this.$route.params);
		},
		/** If `to?.params?.destroy` is undefined, returns true. */
		shouldRemoveFromPageManager(to: Route) {
			return !to.params || to.params.destroy === undefined;
		},
		async afterSelectData(): Promise<void> {
			const page = this.pageMgrPage as PageInfo | null;
			isNotNull(page);
			const { dataMapper } = page;
			if (!dataMapper) {
				return;
			}

			const name = _.last(dataMapper.name.split('/'));
			const mapperName = `after${name!.charAt(0).toUpperCase()}${name!.slice(1)}`;
			const dbToObject = dataMapper.dbToObject as Function;

			if (dataMapper.name && dbToObject) {
				// TODO: JK lepsie otypovat
				const mapperFunction = (<any> this)[mapperName];
				if (typeof mapperFunction === 'function') {
					const data = dbToObject(dataMapper.data);
					await mapperFunction({ data });
				}
			}
			this.$store.commit('pageManager/PAGE_MGR_CLEAR_MAPPER', {});
		},
		loadData() {},
		/** Called after the close button is clicked. */
		async closeClick() {
			await this.$router.back();
		},
		resetStore() {
		},
		refreshFun() {
		},
		/**
		 * Dispatches the PAGE_MGR_BACK action.
		 *
		 * If called, it's before the component is closed and the previous one is opened.
		 */
		async removeFromPageManager(to: Route): Promise<PageInfo[]> {
			return this.pageMagrBack({
				path: to.path,
				refreshPreviousPage: this.refreshPreviousPage,
			});
		},
		/**
		 * Dispatches the PAGE_MGR_LOAD action.
		 *
		 * Called when the component is created.
		 */
		async storeToPageManager() {
			if (this.pageMgrPage?.loadData) {
				this.isExternal && (this.$store.commit(`${this.storeUri}/${SET_PROPS}`, { filter: this.externalOpener?.extraParams }));
				await this.loadData();
				this.pageMgrPage.loadData = false;
				this.pageMgrPage.path = this.$route.path;
				this.pageMgrPage.id = Number(this.$route.params.id);
			}
			if (this.pageMgrPage?.refresh) {
				this.refreshFun();
			}
		},
		/** Calls {@link PAGE_MGR_LOAD} */
		async baseCreated() {
			await this.storeToPageManager();
			await this.afterSelectData();
		},
		async handleRemovingFromPageManager(to: Route) {
			if (this.shouldRemoveFromPageManager(to)) {
				this.modulesToRemove = await this.removeFromPageManager(to);
			}
		},
		/**
		 * custom handling before page leave
		 * @abstract
		 * @param event - beforeUnload event from window
		 */
		async doBeforeUload(event: BeforeUnloadEvent | RouteEvent | null) { },
		/**
		 * handle beforeunload page
		 * @param event - beforeUnload event from window
		 */
		async beforeUnload(event: BeforeUnloadEvent | RouteEvent | null) {
			await this.doBeforeUload(event);
			if (isRouteEvent(event)) {
				await this.handleRemovingFromPageManager(event.to);
				event.next();
			}
		},
		logError(err: Error) {
			this.$store.dispatch(ERROR, { moduleKey: this.storeModule, err });
		},
		async checkDuplicatePage(to: Route) {
			if (!this.shouldRemoveFromPageManager(to)) {
				const crumbIndex = this.$store.state.pageManager.pages.findIndex((page: PageInfo) => page.path === to.path);
				if (crumbIndex !== -1) {
					// otviram stranku, kterou uz mam v page manazeru
					const resp = await this.showConfirm({
						message: this.LocalizeChyboveHlasky('ConfirmDuplicatePage'),
						title: this.LocalizeChyboveHlasky('questionTitle'),
						options: {
							showClose: false,
							confirmButtonText: this.Localize('Ano'),
							cancelButtonText: this.Localize('Ne'),
						},
					});
					if (resp) {
						this.$router.go(-this.$store.state.pageManager.pages.length + crumbIndex + 1);
					}
					return true;
				}
			}
			return false;
		},
	},
	/**
	 * Calls {@link removeFromPageManager} if opposite is not specified (hence, only for Router.back() return actions).
	 * If the page is an editable form, 'removeFromPageManager' handling is delegated to 'beforeRouteLeave' in edit.ts.
	 * */
	async beforeRouteLeave(to, from, next) {
		const isDuplicatePage = await this.checkDuplicatePage(to);
		if (!isDuplicatePage) {
			await this.beforeUnload({ to, from, next });
		}
	},
});
